import React, { useState, useEffect } from 'react'
import { IoIosArrowRoundBack } from 'react-icons/io'
import { useHistory, useLocation } from "react-router-dom"
import { useForm } from 'react-hook-form'
import { OWNERSHIPTYPES } from '../../components/constants'
import api from '../../utils/api'
import { ErrorNotification, SuccessNotification } from '../../utils'
import { FaRegCheckCircle, FaTrash, FaUpload } from 'react-icons/fa'
import { FiPlusCircle } from 'react-icons/fi'
import { trimFormValue } from '../../utils/formValues'
import { useLocationCascade } from '../../hooks/useLocations'

const trimField = { setValueAs: trimFormValue }

const NewFarmView = () => {
  const location = useLocation()
  const history = useHistory()
  const farmer = location.state?.farmer
  const [loading, setLoading] = useState(false)
  const [crops, setCrops] = useState([])
  const [farms, setFarms] = useState([
    {
      id: 0,
      farm_name: '',
      farm_size: '',
      farm_region: '',
      farm_district: '',
      farm_community: '',
      primary_crop: '',
      longitude: '',
      latitude: '',
      ownership: '',
      other_crops: [],
      farm_images: [],
      farm_documents: [],
    }
  ])
  const [currentFarmIndex, setCurrentFarmIndex] = useState(0)
  const { register, handleSubmit, formState: { errors }, setValue, trigger } = useForm()

  const currentFarm = farms[currentFarmIndex]
  const {
    regionNames,
    districts: farmDistricts,
    communities: farmCommunities,
  } = useLocationCascade(currentFarm?.farm_region, currentFarm?.farm_district)

  useEffect(() => {
    api.get('commodities/').then(res => {
      if (res.status === 200 && res.data) setCrops(res.data)
    })
  }, [])

  // Fetch crops, districts, communities as needed (implement as in NewFarmer)
  // ...

  const addFarm = () => {
    const newFarm = {
      id: farms.length,
      farm_name: '',
      farm_size: '',
      farm_region: '',
      farm_district: '',
      farm_community: '',
      primary_crop: '',
      longitude: '',
      latitude: '',
      ownership: '',
      other_crops: [],
      farm_images: [],
      farm_documents: [],
    }
    setFarms([...farms, newFarm])
    setCurrentFarmIndex(farms.length)
  }

  const removeFarm = (index) => {
    if (farms.length > 1) {
      const updatedFarms = farms.filter((_, i) => i !== index)
      setFarms(updatedFarms)
      if (currentFarmIndex >= index) {
        setCurrentFarmIndex(Math.max(0, currentFarmIndex - 1))
      }
    }
  }

  const updateFarm = (index, field, value) => {
    const updatedFarms = [...farms]
    updatedFarms[index] = { ...updatedFarms[index], [field]: value }
    setFarms(updatedFarms)
  }

  const handleFarmImageChange = (e, farmIndex) => {
    const files = Array.from(e.target.files)
    const updatedFarms = [...farms]
    updatedFarms[farmIndex].farm_images = [...updatedFarms[farmIndex].farm_images, ...files]
    setFarms(updatedFarms)
  }

  const handleFarmDocumentChange = (e, farmIndex) => {
    const files = Array.from(e.target.files)
    const updatedFarms = [...farms]
    updatedFarms[farmIndex].farm_documents = [...updatedFarms[farmIndex].farm_documents, ...files]
    setFarms(updatedFarms)
  }

  const removeFarmImage = (farmIndex, imageIndex) => {
    const updatedFarms = [...farms]
    updatedFarms[farmIndex].farm_images = updatedFarms[farmIndex].farm_images.filter((_, i) => i !== imageIndex)
    setFarms(updatedFarms)
  }

  const removeFarmDocument = (farmIndex, docIndex) => {
    const updatedFarms = [...farms]
    updatedFarms[farmIndex].farm_documents = updatedFarms[farmIndex].farm_documents.filter((_, i) => i !== docIndex)
    setFarms(updatedFarms)
  }

  const toggleFarmCrop = (farmIndex, cropId) => {
    const updatedFarms = [...farms]
    const currentCrops = updatedFarms[farmIndex].other_crops
    if (currentCrops.includes(cropId)) {
      updatedFarms[farmIndex].other_crops = currentCrops.filter(id => id !== cropId)
    } else {
      updatedFarms[farmIndex].other_crops = [...currentCrops, cropId]
    }
    setFarms(updatedFarms)
  }

  // Remove validateFarms and toast-based validation
  const onSubmit = async (e) => {
    e.preventDefault()
    const valid = await trigger()
    if (!valid) return
    if (!farmer?.id) {
      ErrorNotification('No farmer selected')
      return
    }
    setLoading(true)
    try {
      // Build farms_data payload
      const farms_data = farms.map((farm, i) => ({
        farm_name: trimFormValue(farm.farm_name),
        farm_size: parseFloat(farm.farm_size),
        region: farm.farm_region,
        district: farm.farm_district,
        community: farm.farm_community,
        production_capacity: parseFloat(farm.production_capacity) || 0,
        primary_crop: farm.primary_crop,
        ownership_type: farm.ownership,
        crop_type_ids: farm.other_crops,
      }))

      // POST farms
      const resp = await api.post(
        // `farmers/${farmer.id}/add_farm/`,
        `farmer-farms/bulk_create/`,
        JSON.stringify({
          farmer_id: farmer?.id,
          farms: farms_data
        }),
        { headers: { 'Content-Type': 'application/json' } }
      )
      // Upload images/docs for each farm if needed
      if (resp?.data?.my_farms) {
        const createdFarms = resp.data.my_farms
        for (let i = 0; i < farms.length; i++) {
          const farm = farms[i]
          const farmId = createdFarms[i]?.id
          if (farmId && (farm.farm_images.length > 0 || farm.farm_documents.length > 0)) {
            const imageFormData = new FormData()
            const docsData = new FormData()


            farm.farm_images.forEach((file) => {
              imageFormData.append('images', file)
            })

            docsData.append('farm_document', farm.farm_documents[0])

            imageFormData.append('farm_id', farmId.toString())
            docsData.append('farm_id', farmId.toString())

            await api.post(
              `farmers/${farmer.id}/upload_farm_images/`,
              imageFormData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            )

            if (farm.farm_documents?.length > 0) {
              await api.patch(
                `/farmers/${farmer?.id}/update_farm/`,
                docsData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
              )
            }
          }
        }
      }
      setLoading(false)
      SuccessNotification('Farm(s) added successfully')
      history.goBack()
    } catch (error) {
      setLoading(false)
      ErrorNotification('Failed to add farm(s)')
    }
  }

  return (
    <div className="w-full lg:px-10 px-2 h-full py-6">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => history.goBack()} className="mb-3 flex flex-row items-center">
          <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
          <p className="text-sm font-light capitalize dark:text-white">
            Back
          </p>
        </button>
      </div>
      <h1 className="text-lg font-bold capitalize mb-5 dark:text-white">Add Farm(s) for {farmer ? `${farmer.firstname} ${farmer.lastname}` : 'Farmer'}</h1>
      <form className="bg-white h-full px-4 py-2 mt-2 rounded-lg dark:bg-dark" onSubmit={onSubmit}>
        <div className="h-[80vh] w-full py-3 overflow-y-auto">
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={addFarm}
              className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 rounded-md capitalize"
            >
              <FiPlusCircle size={20} />
              Add Farm
            </button>
          </div>
          {farms.map((farm, index) => (
            <div key={farm.id} className="my-4 border rounded-lg dark:border-border-dark">
              {/* Header */}
              <div
                className={`flex items-center justify-between px-4 py-3 cursor-pointer ${currentFarmIndex === index ? 'bg-yellowcolor/30' : 'bg-gray-100 dark:bg-secondaryDark'}`}
                onClick={() => setCurrentFarmIndex(index)}
              >
                <div>
                  <span className="font-semibold dark:text-white">Farm {index + 1}</span>
                  {farm.farm_name && (
                    <span className="ml-2 text-gray-600 dark:text-white">({farm.farm_name})</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {farms.length > 1 && (
                    <button type="button" onClick={() => removeFarm(index)} className="text-red-500 hover:text-red-700">
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>
              {/* Expanded Form */}
              {currentFarmIndex === index && (
                <div className="p-4 bg-white dark:bg-dark">
                  <div className="flex flex-col space-y-4 md:grid md:grid-cols-2 md:gap-7">
                    <div className="w-full space-y-3 mt-4">
                      <label className="text-sm font-normal capitalize dark:text-white">Farm Name</label>
                      <input
                        type="text"
                        placeholder="Enter your farm name"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        value={farm.farm_name || ""}
                        {...register(`farm_name_${index}`, { ...trimField, required: 'Farm name is required' })}
                        onChange={e => { updateFarm(index, "farm_name", e.target.value); setValue(`farm_name_${index}`, e.target.value, { shouldValidate: true }) }}
                        onBlur={e => {
                          const trimmed = e.target.value.trim()
                          updateFarm(index, "farm_name", trimmed)
                          setValue(`farm_name_${index}`, trimmed, { shouldValidate: true })
                        }}
                      />
                      {errors[`farm_name_${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`farm_name_${index}`].message}</p>}
                    </div>
                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">Farm Size (Acres)<span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        min={1}
                        placeholder="Enter your farm size"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        value={farm.farm_size || ""}
                        {...register(`farm_size_${index}`, { required: 'Farm size is required' })}
                        onChange={e => { updateFarm(index, "farm_size", e.target.value); setValue(`farm_size_${index}`, e.target.value, { shouldValidate: true }) }}
                      />
                      {errors[`farm_size_${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`farm_size_${index}`].message}</p>}
                    </div>
                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">Region<span className="text-red-500">*</span></label>
                      <select
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        {...register(`farm_region_${index}`, { required: 'Region is required' })}
                        onChange={e => {
                          const updatedFarms = [...farms]
                          updatedFarms[index] = {
                            ...updatedFarms[index],
                            farm_region: e.target.value,
                            farm_district: '',
                            farm_community: '',
                          }
                          setFarms(updatedFarms)
                          setValue(`farm_region_${index}`, e.target.value, { shouldValidate: true })
                          setValue(`farm_district_${index}`, '', { shouldValidate: true })
                          setValue(`farm_community_${index}`, '', { shouldValidate: true })
                        }}
                        defaultValue={farm.farm_region || ""}
                      >
                        <option value="">Select Region</option>
                        {regionNames.map((regionName, idx) => (
                          <option key={idx} value={regionName}>{regionName}</option>
                        ))}
                      </select>
                      {errors[`farm_region_${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`farm_region_${index}`].message}</p>}
                    </div>
                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">District<span className="text-red-500">*</span></label>
                      <select
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        {...register(`farm_district_${index}`, { required: 'District is required' })}
                        onChange={e => {
                          const updatedFarms = [...farms]
                          updatedFarms[index] = {
                            ...updatedFarms[index],
                            farm_district: e.target.value,
                            farm_community: '',
                          }
                          setFarms(updatedFarms)
                          setValue(`farm_district_${index}`, e.target.value, { shouldValidate: true })
                          setValue(`farm_community_${index}`, '', { shouldValidate: true })
                        }}
                        defaultValue={farm.farm_district || ""}
                      >
                        <option value="">Select District</option>
                        {farmDistricts?.map((item, idx) => (
                          <option key={idx} value={item.name}>{item.name}</option>
                        ))}
                      </select>
                      {errors[`farm_district_${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`farm_district_${index}`].message}</p>}
                    </div>
                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">Community<span className="text-red-500">*</span></label>
                      <select
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        {...register(`farm_community_${index}`, { required: 'Community is required' })}
                        onChange={e => { updateFarm(index, "farm_community", e.target.value); setValue(`farm_community_${index}`, e.target.value, { shouldValidate: true }) }}
                        defaultValue={farm.farm_community || ""}
                      >
                        <option value="">Select Community</option>
                        {farmCommunities?.map((item, idx) => (
                          <option key={idx} value={item}>{item}</option>
                        ))}
                      </select>
                      {errors[`farm_community_${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`farm_community_${index}`].message}</p>}
                    </div>
                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">Primary Commodity<span className="text-red-500">*</span></label>
                      <select
                        type="text"
                        placeholder="Enter primary commodity"
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                        value={farm.primary_crop || ""}
                        {...register(`primary_crop_${index}`, { required: 'Primary commodity is required' })}
                        onChange={e => { updateFarm(index, "primary_crop", e.target.value); setValue(`primary_crop_${index}`, e.target.value, { shouldValidate: true }) }}
                      >
                        <option>Select Primary Commodity</option>
                        {crops?.map((item) => <option className="capitalize" value={item?.name}>{item?.name}</option>)}
                      </select>
                      {errors[`primary_crop_${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`primary_crop_${index}`].message}</p>}
                    </div>
                   
                    <div className="w-full space-y-3">
                      <label className="text-sm font-normal capitalize dark:text-white">Ownership Type<span className="text-red-500">*</span></label>
                      <select
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent capitalize placeholder-gray-400"
                        {...register(`ownership_${index}`, { required: 'Ownership type is required' })}
                        onChange={e => { updateFarm(index, "ownership", e.target.value); setValue(`ownership_${index}`, e.target.value, { shouldValidate: true }) }}
                        defaultValue={farm.ownership || ""}
                      >
                        <option>Select Ownership Type</option>
                        {OWNERSHIPTYPES?.map((item) => <option className="capitalize" value={item}>{item}</option>)}
                      </select>
                      {errors[`ownership_${index}`] && <p className="text-red-500 text-xs mt-1">{errors[`ownership_${index}`].message}</p>}
                    </div>
                    <div></div>
                    <div className="w-full space-y-3 col-span-2">
                      <label className="text-sm font-normal capitalize dark:text-white">Add Other Crops/ Commodities</label>
                      <div className="flex flex-row items-center gap-4 flex-wrap">
                        {crops?.map((item, idx) => (
                          <button type="button" key={idx} className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-full py-2 px-3 flex flex-row items-center gap-2 text-sm" onClick={() => toggleFarmCrop(index, item?.id)}>
                            <p className="text-black dark:text-white">{item?.name}</p>
                            {farm.other_crops.includes(item?.id) ? <FaRegCheckCircle className="w-4 h-4 text-yellowcolor" /> : <div className="w-4 h-4 border border-gray-500 rounded-full" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="w-full space-y-3 mt-5">
                    <label className="text-sm font-normal capitalize dark:text-white">Upload photos (Farm Images)</label>
                    <div className="bg-gray-100 h-52 flex items-center justify-center rounded-md p-4 dark:bg-secondaryDark">
                      <label htmlFor={`farm_images_${index}`} className="cursor-pointer">
                        <div className="bg-white px-20 border border-dashed border-gray-200 rounded-md p-4 flex flex-col items-center justify-center gap-4 dark:bg-dark dark:border-border-dark dark:border-border-dark">
                          <FaUpload size={20} className="text-yellowcolor" />
                          <p className="text-sm text-black dark:text-white font-light text-center">Browse or drag and drop farm photos you want <br /> to upload from your computer</p>
                          <p className="text-base text-yellowcolor underline text-center">Click to upload</p>
                        </div>
                        <input type="file" id={`farm_images_${index}`} className="hidden" accept="image/png,image/jpeg" multiple onChange={e => handleFarmImageChange(e, index)} onKeyDown={e => { if (e.key === "Enter") e.preventDefault() }} />
                      </label>
                    </div>
                  </div>
                  {farm.farm_images?.length > 0 && <div className="w-full mt-5">
                    <div className="bg-gray-100 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 rounded-md p-4 dark:bg-secondaryDark">
                      {farm.farm_images?.map((item, imgIdx) => (
                        <div key={imgIdx} className="bg-white h-32 relative group rounded-md overflow-hidden">
                          <img src={URL.createObjectURL(item)} alt="farm image" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => removeFarmImage(index, imgIdx)} className="absolute top-0 right-0 bg-black/50 text-red-500 p-2 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>}
                  <hr className="my-5 border-gray-200" />
                  <div className="w-full space-y-3">
                    <label className="text-sm font-normal capitalize dark:text-white">Farm Documents (Optional)</label>
                    <div className="bg-gray-100 h-52 flex items-center justify-center rounded-md p-4 dark:bg-secondaryDark">
                      <label htmlFor={`farm_documents_${index}`} className="cursor-pointer">
                        <div className="bg-white px-20 border border-dashed border-gray-200 rounded-md p-4 flex flex-col items-center justify-center gap-4 dark:bg-dark dark:border-border-dark dark:border-border-dark">
                          <FaUpload size={20} className="text-yellowcolor" />
                          <p className="text-sm text-black dark:text-white font-light text-center">Browse or drag and drop farm documents.</p>
                          <p className="text-base text-yellowcolor underline text-center">Click to upload</p>
                        </div>
                        <input type="file" id={`farm_documents_${index}`} className="hidden" accept="application/pdf" multiple onChange={e => handleFarmDocumentChange(e, index)} />
                      </label>
                    </div>
                  </div>
                  {farm.farm_documents?.length > 0 && <div className="w-full mt-5">
                    <div className="bg-gray-100 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 rounded-md p-4 dark:bg-secondaryDark">
                      {farm.farm_documents?.map((item, docIdx) => (
                        <div key={docIdx} className="bg-white h-32 relative group rounded-md overflow-hidden flex flex-col items-center justify-center p-2">
                          <div className="flex flex-col items-center justify-center w-full h-full">
                            <FaUpload size={20} className="text-yellowcolor" />
                            <span className="block text-xs text-center mt-2 truncate w-24" title={item.name}>{item.name}</span>
                          </div>
                          <a href={URL.createObjectURL(item)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline mt-1">View</a>
                          <button type="button" onClick={() => removeFarmDocument(index, docIdx)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"><svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M6 6L14 14M6 14L14 6" stroke="white" strokeWidth="2" strokeLinecap="round" /></svg></button>
                        </div>
                      ))}
                    </div>
                  </div>}
                  <div className="h-8"></div>
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Submit button and loading state */}
        <div className="flex flex-row items-end justify-end gap-4 mt-8">
          <button
            type="submit"
            className="px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Farm(s)'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default NewFarmView